import {
  createStudioPluginRegistry,
} from "./StudioPluginRegistry";

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim() !== "";
}

function assertPluginRegistry(pluginRegistry) {
  const requiredMethods = [
    "register",
    "unregister",
    "get",
    "has",
    "list",
    "clear",
  ];

  if (
    !pluginRegistry
    || typeof pluginRegistry !== "object"
    || requiredMethods.some(
      (methodName) => typeof pluginRegistry[methodName] !== "function"
    )
    || !pluginRegistry.capabilityRegistry
  ) {
    throw new TypeError(
      "StudioRuntime.pluginRegistry must be a valid StudioPluginRegistry."
    );
  }

  return pluginRegistry;
}

function normalizePlugins(plugins) {
  if (!Array.isArray(plugins)) {
    throw new TypeError(
      "StudioRuntime.plugins must be an array."
    );
  }

  return plugins;
}

/**
 * Creates one isolated runtime for one Studio host.
 *
 * The runtime remains independent from React and from every business domain.
 * It owns the plugin registry and exposes capability resolution operations.
 */
export function createStudioRuntime({
  pluginRegistry = createStudioPluginRegistry(),
  plugins = [],
} = {}) {
  const registry = assertPluginRegistry(pluginRegistry);
  const initialPlugins = normalizePlugins(plugins);
  const registeredInitialPluginIds = [];

  try {
    initialPlugins.forEach((plugin) => {
      registry.register(plugin);
      registeredInitialPluginIds.push(plugin.id);
    });
  } catch (error) {
    registeredInitialPluginIds
      .slice()
      .reverse()
      .forEach((pluginId) => {
        registry.unregister(pluginId);
      });

    throw error;
  }

  function registerPlugin(plugin) {
    return registry.register(plugin);
  }

  function unregisterPlugin(pluginId) {
    return registry.unregister(pluginId);
  }

  function getPlugin(pluginId) {
    return registry.get(pluginId);
  }

  function hasPlugin(pluginId) {
    return registry.has(pluginId);
  }

  function listPlugins() {
    return registry.list();
  }

  function getCapability(capabilityId) {
    return registry.capabilityRegistry.get(capabilityId);
  }

  function hasCapability(capabilityId) {
    return registry.capabilityRegistry.has(capabilityId);
  }

  function listCapabilities() {
    return registry.capabilityRegistry.list();
  }

  function resolveCapabilities(type) {
    if (!isNonEmptyString(type)) {
      throw new TypeError(
        "StudioRuntime capability type must be a non-empty string."
      );
    }

    return registry.capabilityRegistry.listByType(type.trim());
  }

  function resolveContributions(type) {
    return Object.freeze(
      resolveCapabilities(type).map(
        (capability) => capability.contribution
      )
    );
  }

  function clear() {
    return registry.clear();
  }

  return Object.freeze({
    registerPlugin,
    unregisterPlugin,
    getPlugin,
    hasPlugin,
    listPlugins,
    getCapability,
    hasCapability,
    listCapabilities,
    resolveCapabilities,
    resolveContributions,
    clear,
    pluginRegistry: registry,
    capabilityRegistry: registry.capabilityRegistry,
  });
}

export function isStudioRuntime(value) {
  return Boolean(
    value
      && typeof value === "object"
      && typeof value.registerPlugin === "function"
      && typeof value.unregisterPlugin === "function"
      && typeof value.getPlugin === "function"
      && typeof value.hasPlugin === "function"
      && typeof value.listPlugins === "function"
      && typeof value.getCapability === "function"
      && typeof value.hasCapability === "function"
      && typeof value.listCapabilities === "function"
      && typeof value.resolveCapabilities === "function"
      && typeof value.resolveContributions === "function"
      && value.pluginRegistry
      && value.capabilityRegistry
  );
}

export function assertStudioRuntime(value) {
  if (!isStudioRuntime(value)) {
    throw new TypeError(
      "Expected a valid StudioRuntime."
    );
  }

  return value;
}
